import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Download,
  ExternalLink,
  KeyRound,
  LifeBuoy,
  Loader2,
  Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useRecoveryActions,
  useRequestManualFix,
} from "@/hooks/useTechnicalAuditIssues";

// Route to the WordPress connection card (secret-key connect / credentials) —
// the "integrations" tab of the workspace settings page. Used by the failed-fix
// recovery actions ("Reconnect site" / "Update WordPress credentials").
const WP_SETTINGS_ROUTE = "/workspace-settings?tab=integrations";

interface RecoveryActionsProps {
  issueId: string;
  /** Only fetches + renders when the fix has failed. */
  isFailed: boolean;
  /** Table-row variant: tighter buttons + shorter labels so the row isn't
   *  overloaded. Defaults to the full drawer-banner variant. */
  compact?: boolean;
  /** Runs before navigating to WordPress settings (e.g. close the drawer). */
  onNavigate?: () => void;
  /** Extra classes for the button row wrapper. */
  className?: string;
}

/**
 * Context-aware recovery actions for a FAILED technical-audit fix apply.
 *
 * Fetches the backend's failure classification via `useRecoveryActions` (which
 * ALSO returns the CURRENT `apply_error` from the DB) and renders the matching
 * one-click recovery buttons (download the plugin, reconnect the site, open WP
 * Admin, or update credentials). Returns `null` when the fix hasn't failed, or
 * when there is neither a fresh error nor an actionable recovery kind (so it
 * never renders an empty box).
 *
 * Two variants:
 *   - **full** (default, drawer/dialog banner): owns the whole destructive
 *     banner — AlertTriangle icon + the fresh "The last apply failed: …" text
 *     + the recovery buttons below. Reading `apply_error` from the hook keeps it
 *     ALWAYS current, so dialogs that hold a snapshot `issue` prop no longer
 *     show a stale error after a retry.
 *   - **compact** (issues table row): renders ONLY the buttons — the row
 *     already shows the (fresh) error text on its own, so no banner here.
 *
 * Each instance calls the hook independently; React Query dedupes (staleTime
 * 30s) so repeated failed rows share one request per issue.
 */
export function RecoveryActions({
  issueId,
  isFailed,
  compact = false,
  onNavigate,
  className,
}: RecoveryActionsProps) {
  const navigate = useNavigate();
  const { data: recoveryActions } = useRecoveryActions(issueId, isFailed);
  const requestManualFix = useRequestManualFix();

  const applyError = recoveryActions?.apply_error ?? null;
  const hasKind = Boolean(recoveryActions?.kind);

  // A failed fix always offers "Request expert fix" (below), so render whenever
  // the fix has failed — never an empty box.
  if (!isFailed) return null;

  const goToWpSettings = () => {
    onNavigate?.();
    navigate(WP_SETTINGS_ROUTE);
  };

  // Compact rows shrink the buttons (h-7) and drop the longer wording so the
  // action fits alongside Retry / Dismiss / Verify.
  const compactBtn = compact ? "h-7 text-xs" : "";
  const iconSize = compact ? "h-3 w-3" : "h-3.5 w-3.5";

  const wrapperClass = [
    "flex flex-wrap items-center gap-2",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  // "Request expert fix" — always available on a failed fix. Files a ticket to the
  // AgileCo team and moves the issue to the calm "Expert fix requested" state, so a
  // user who can't self-serve a fix has a clear next step instead of a dead end.
  const manualFixBtn = (
    <Button
      type="button"
      size="sm"
      variant={hasKind ? "outline" : "default"}
      disabled={requestManualFix.isPending}
      onClick={() => requestManualFix.mutate(issueId)}
      className={`gap-1.5 ${hasKind ? "" : "bg-coral text-white hover:bg-coral-hover"} ${compactBtn}`}
    >
      {requestManualFix.isPending ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : (
        <LifeBuoy className={iconSize} />
      )}
      {compact ? "Get expert fix" : "Request expert fix"}
    </Button>
  );

  // The kind-specific recovery buttons render only when the backend classified an
  // actionable recovery kind; the expert-fix button is always the fallback.
  const buttons = (
    <div className={wrapperClass}>
      {recoveryActions?.kind === "plugin_missing" && (
        <>
          {recoveryActions?.plugin_download_url && (
            <Button
              asChild
              size="sm"
              className={`gap-1.5 bg-coral text-white hover:bg-coral-hover ${compactBtn}`}
            >
              <a href={recoveryActions?.plugin_download_url} download>
                <Download className={iconSize} />
                {recoveryActions?.plugin_version
                  ? compact
                    ? `Plugin v${recoveryActions?.plugin_version}`
                    : `Download plugin v${recoveryActions?.plugin_version}`
                  : compact
                    ? "Plugin"
                    : "Download plugin"}
              </a>
            </Button>
          )}
          {recoveryActions?.wp_admin_url && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className={`gap-1.5 ${compactBtn}`}
            >
              <a
                href={recoveryActions?.wp_admin_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className={iconSize} />
                {compact ? "WP Admin" : "Open WP Admin → Plugins"}
              </a>
            </Button>
          )}
        </>
      )}
      {recoveryActions?.kind === "key_mismatch" && (
        <>
          <Button
            type="button"
            size="sm"
            onClick={goToWpSettings}
            className={`gap-1.5 bg-coral text-white hover:bg-coral-hover ${compactBtn}`}
          >
            <Plug className={iconSize} />
            {compact ? "Reconnect" : "Reconnect site"}
          </Button>
          {recoveryActions?.wp_admin_url && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className={`gap-1.5 ${compactBtn}`}
            >
              <a
                href={recoveryActions?.wp_admin_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className={iconSize} />
                {compact ? "WP Admin" : "Open WP Admin"}
              </a>
            </Button>
          )}
        </>
      )}
      {recoveryActions?.kind === "creds_invalid" && (
        <Button
          type="button"
          size="sm"
          onClick={goToWpSettings}
          className={`gap-1.5 bg-coral text-white hover:bg-coral-hover ${compactBtn}`}
        >
          <KeyRound className={iconSize} />
          {compact ? "Update credentials" : "Update WordPress credentials"}
        </Button>
      )}
      {/* In the drawer/dialog the expert-fix action is the PRIMARY button (it
          replaces Apply for manual failures), so only the compact table-row
          variant renders it here — avoids a duplicate button in the drawer. */}
      {compact && manualFixBtn}
    </div>
  );

  // Compact (table row): buttons only — the row already shows the fresh error.
  if (compact) return buttons;

  // Full (drawer/dialog): own the whole destructive banner, with the FRESH
  // error text (falls back to nothing when apply_error is null) + buttons below.
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        {applyError && (
          <div className="text-destructive/90">
            The last apply failed: {applyError}
          </div>
        )}
        {buttons}
      </div>
    </div>
  );
}
